import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mic, Play, Pause, Loader2, Users, Send, Quote, MessageCircle, Sparkles, UserPlus, CalendarClock, BellRing, Info, ChevronDown, QrCode } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";
import { getVoterId } from "@/lib/voter";

export function Conference() {
  const { t, i18n } = useTranslation();
  const [conf, setConf] = useState(null);
  const [count, setCount] = useState(0);
  const [registered, setRegistered] = useState(false);
  const [interventions, setInterventions] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [notifying, setNotifying] = useState("");
  const [openSummary, setOpenSummary] = useState("");
  const [live, setLive] = useState({ status: "live", starts_at: null, ends_at: null });
  const [online, setOnline] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [recentName, setRecentName] = useState("");
  const lastAtRef = useRef("");
  const chatEndRef = useRef(null);
  const myVid = getVoterId();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [ivName, setIvName] = useState("");
  const [ivContent, setIvContent] = useState("");
  const [ivSending, setIvSending] = useState(false);

  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);

  useEffect(() => {
    const vid = getVoterId();
    api.get(`/conference/current?voter_id=${encodeURIComponent(vid)}&lang=${i18n.language}`)
      .then(({ data }) => {
        setConf(data);
        setCount(data.registered_count || 0);
        setRegistered(!!data.registered);
        const ivs = data.interventions || [];
        setInterventions(ivs);
        setUpcoming(data.upcoming || []);
        if (data.live) setLive(data.live);
        setOnline(data.online || 0);
        lastAtRef.current = ivs.length ? ivs[ivs.length - 1].created_at : "";
      })
      .catch(() => setConf(null));
    return () => { if (audioRef.current) { audioRef.current.pause(); } };
  }, [i18n.language]);

  // clock for countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Deep-link: /?conf=<id> opens that conference's summary + scrolls to the Agora zone
  useEffect(() => {
    if (!conf) return;
    const wanted = new URLSearchParams(window.location.search).get("conf");
    if (!wanted) return;
    setOpenSummary(wanted);
    setTimeout(() => {
      const el = document.getElementById("zone-agora") || document.getElementById("conference");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 500);
  }, [conf]);

  // live polling: new messages + status + presence count
  useEffect(() => {
    if (!conf) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/conference/live`, { params: { since: lastAtRef.current, voter_id: myVid } });
        if (stop) return;
        if (data.live) setLive(data.live);
        setOnline(data.online || 0);
        const msgs = data.messages || [];
        if (msgs.length) {
          lastAtRef.current = msgs[msgs.length - 1].created_at;
          setInterventions((prev) => [...prev, ...msgs]);
          const fromOthers = msgs.filter((m) => m.voter_id !== myVid);
          if (fromOthers.length) {
            const nm = fromOthers[fromOthers.length - 1].name || "Quelqu'un";
            setRecentName(nm);
            setTimeout(() => { if (!stop) setRecentName(""); }, 6000);
          }
        }
      } catch { /* transient polling error, keep going */ }
    };
    const id = setInterval(poll, 3500);
    return () => { stop = true; clearInterval(id); };
  }, [conf, myVid]);

  // presence heartbeat while live
  useEffect(() => {
    if (!conf || live.status !== "live") return;
    let stop = false;
    const beat = async () => {
      try { const { data } = await api.post("/conference/presence", { voter_id: myVid, name: ivName.trim() }); if (!stop) setOnline(data.online || 0); }
      catch { /* ignore */ }
    };
    beat();
    const id = setInterval(beat, 10000);
    return () => { stop = true; clearInterval(id); };
  }, [conf, live.status, myVid, ivName]);

  // auto-scroll chat to bottom on new message
  useEffect(() => {
    if (live.status === "live" && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [interventions.length, live.status]);

  const register = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/conference/register", { voter_id: getVoterId(), name: name.trim(), email: email.trim(), lang: (i18n.language || "fr").slice(0, 2) });
      setCount(data.registered_count);
      setRegistered(true);
      setShowForm(false);
      toast.success(t("conf.registeredToast"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const sendIntervention = async () => {
    const content = ivContent.trim();
    if (!content || ivSending) return;
    setIvSending(true);
    try {
      const { data } = await api.post("/conference/intervene", { voter_id: myVid, name: ivName.trim(), content });
      setInterventions((prev) => [...prev, data]);
      lastAtRef.current = data.created_at;
      setIvContent("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setIvSending(false);
    }
  };

  const inviteFriends = async () => {
    const text = t("conf.inviteText", { title: conf.title });
    const url = window.location.origin;
    if (navigator.share) {
      try { await navigator.share({ title: "COCOON", text, url }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success(t("conf.inviteCopied"));
    } catch { /* ignore */ }
  };

  const notifyMe = async (confId) => {
    if (notifying) return;
    setNotifying(confId);
    try {
      const { data } = await api.post("/conference/notify", { voter_id: getVoterId(), conf_id: confId });
      setUpcoming((prev) => prev.map((u) => u.id === confId ? { ...u, notified: true, notify_count: data.notify_count } : u));
      toast.success(t("conf.notifiedToast"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setNotifying("");
    }
  };

  const toggleAudio = async () => {
    if (audioState === "playing") { audioRef.current?.pause(); setAudioState("paused"); return; }
    if (audioState === "paused" && audioRef.current) { audioRef.current.play(); setAudioState("playing"); return; }
    setAudioState("loading");
    try {
      const { data } = await api.get(`/conference/audio?lang=${i18n.language}`);
      const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setAudioState("idle");
      audio.onpause = () => setAudioState((s) => (s === "playing" ? "paused" : s));
      await audio.play();
      setAudioState("playing");
    } catch {
      setAudioState("idle");
      toast.error(t("conf.loading"));
    }
  };

  if (!conf) return null;

  const fmtLiveDate = (iso) => {
    try { return new Date(iso).toLocaleString(i18n.language, { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  const summaryLabel = ({ fr: "Résumé", en: "Summary", es: "Resumen", nl: "Samenvatting" })[(i18n.language || "fr").slice(0, 2)] || "Résumé";
  const cd = (() => {
    if (!live.starts_at) return null;
    const diff = new Date(live.starts_at).getTime() - now;
    if (diff <= 0) return null;
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  })();

  return (
    <section className="max-w-4xl mx-auto px-6 py-14 sm:py-24" data-testid="conference-section">
      {/* header */}
      <div className="text-center mb-10">
        <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
          <Mic size={13} /> {t("conf.monthLabel")} · {conf.month}
        </p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-gradient-orange leading-tight" data-testid="conference-title">{conf.title}</h2>
        {conf.subtitle && <p className="mt-4 text-base text-foreground/60 max-w-2xl mx-auto">{conf.subtitle}</p>}
        <Link
          to={`/invitation?ev=conf&id=${encodeURIComponent(conf.id)}`}
          data-testid="conference-current-eventqr"
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-primary/75 hover:text-primary transition-colors border border-primary/30 rounded-full px-4 py-1.5"
        >
          <QrCode size={13} /> {t("conf.eventQr", "QR d'événement")}
        </Link>
      </div>

      {/* Panneau d'inscription + compte à rebours (homepage) */}
      {live.status === "before" && cd && (
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="glass frame-pulse rounded-3xl p-7 sm:p-10 mb-12 text-center" data-testid="conference-register-panel"
        >
          <p className="inline-flex items-center gap-2 text-primary/80 uppercase tracking-[0.2em] text-xs mb-4">
            <BellRing size={14} /> {t("conf.regPanelTitle")}
          </p>
          <p className="text-sm text-foreground/55 mb-1">{t("conf.regStartsIn")}</p>
          <p className="inline-flex items-center gap-2 text-foreground/60 text-sm mb-6">
            <CalendarClock size={14} className="text-primary/70" /> {fmtLiveDate(live.starts_at)}
          </p>
          <div className="flex items-center justify-center gap-3 sm:gap-7 mb-8" data-testid="conference-panel-countdown">
            {[["d", cd.d], ["h", cd.h], ["m", cd.m], ["s", cd.s]].map(([u, v]) => (
              <div key={u} className="flex flex-col items-center">
                <span className="font-display text-gradient-orange text-4xl sm:text-6xl tabular-nums">{String(v).padStart(2, "0")}</span>
                <span className="text-[11px] uppercase tracking-widest text-foreground/40 mt-1">{t(`conf.${u}Unit`)}</span>
              </div>
            ))}
          </div>
          {registered ? (
            <span data-testid="conference-panel-registered" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-primary/40 text-primary text-sm font-semibold">
              <Sparkles size={16} /> {t("conf.registered")}
            </span>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              data-testid="conference-panel-register"
              className="btn-frost-orange inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm"
            >
              <Users size={16} /> {t("conf.register")}
            </button>
          )}
        </motion.div>
      )}

      {/* cover */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
        className="relative rounded-3xl overflow-hidden frame-pulse mb-10"
        data-testid="conference-cover"
      >
        <CinematicVideo
          fade
          sound
          soundMask
          src="https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/0q2w9p73_gemini_generated_video_4490dce8.mp4"
          poster={conf.cover_url}
          testId="conference-cover-video"
          caption={conf.title}
        />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-background/70 via-transparent to-transparent" />
      </motion.div>

      {/* actions bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-12">
        <button
          onClick={toggleAudio}
          data-testid="conference-listen-btn"
          className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm flex-1"
        >
          {audioState === "loading" ? <Loader2 size={17} className="animate-spin" /> : audioState === "playing" ? <Pause size={17} /> : <Play size={17} />}
          {audioState === "loading" ? t("conf.loading") : audioState === "playing" ? t("conf.pause") : t("conf.listen")}
        </button>
        {registered ? (
          <span data-testid="conference-registered" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-primary/40 text-primary text-sm font-semibold flex-1">
            <Sparkles size={16} /> {t("conf.registered")}
          </span>
        ) : (
          <button
            onClick={() => setShowForm((s) => !s)}
            data-testid="conference-register-toggle"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-primary/40 text-primary text-sm font-semibold hover:bg-primary/10 transition-colors flex-1"
          >
            <Users size={16} /> {t("conf.register")}
          </button>
        )}
        <span className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full glass text-sm text-foreground/70 whitespace-nowrap" data-testid="conference-count">
          <Users size={15} className="text-primary/70" /> {t("conf.count", { count })}
        </span>
      </div>

      {/* invite CTA */}
      <motion.button
        onClick={inviteFriends}
        data-testid="conference-invite-btn"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className="w-full glass frame-pulse rounded-2xl px-6 py-5 mb-12 flex items-center justify-center gap-3 text-primary hover:bg-primary/5 transition-colors"
      >
        <UserPlus size={20} />
        <span className="font-display italic text-xl sm:text-2xl">{t("conf.invite")}</span>
      </motion.button>

      {/* register form */}
      {showForm && !registered && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="glass rounded-2xl p-6 mb-12 frame-pulse" data-testid="conference-register-form">
          <p className="text-sm text-foreground/60 mb-4">{t("conf.registerDesc")}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("conf.namePlaceholder")} data-testid="conference-name-input"
              className="flex-1 bg-black/30 border border-white/10 rounded-full px-5 py-3 outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("conf.emailPlaceholder")} data-testid="conference-email-input"
              className="flex-1 bg-black/30 border border-white/10 rounded-full px-5 py-3 outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors" />
            <button onClick={register} disabled={submitting} data-testid="conference-register-confirm"
              className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-60 whitespace-nowrap">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />} {t("conf.confirm")}
            </button>
          </div>
        </motion.div>
      )}

      {/* intro */}
      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
        className="relative rounded-[1.75rem] border border-primary/20 glass frame-pulse px-6 sm:px-10 py-8 sm:py-10 mb-16" data-testid="conference-intro-frame">
        <p className="font-display italic text-xl sm:text-2xl text-foreground/80 leading-relaxed" data-testid="conference-intro">
          {conf.intro}
        </p>
      </motion.div>

      {/* chapters */}
      <div className="space-y-12 mb-16">
        {conf.chapters.map((ch, i) => (
          <motion.article key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.05 }}
            style={{ animationDelay: `${(i % 4) * 0.6}s` }} className="glass frame-pulse rounded-2xl p-6 sm:p-10 break-words" data-testid={`conference-chapter-${i}`}>
            <div className="flex items-start gap-4 sm:gap-5 mb-5">
              <span className="font-display text-3xl sm:text-5xl text-primary/60 leading-none flex-shrink-0">{ch.num}</span>
              <h3 className="font-display text-xl sm:text-3xl leading-snug pt-1 break-words">{ch.title}</h3>
            </div>
            {(ch.body || []).map((p, j) => (
              <p key={j} className="text-foreground/70 leading-[1.85] mb-4">{p}</p>
            ))}
            {ch.highlights && (
              <ul className="space-y-3 mt-4">
                {ch.highlights.map((h, k) => (
                  <li key={k} className="flex items-start gap-3 text-foreground/75 leading-relaxed">
                    <Sparkles size={16} className="text-primary mt-1 flex-shrink-0" /> <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}
            {ch.table && (
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {ch.table.map((row, k) => (
                  <div key={k} className="rounded-xl border border-primary/20 bg-primary/[0.04] p-5">
                    <p className="font-display text-lg text-primary mb-2">{row.label}</p>
                    <p className="text-sm text-foreground/65 leading-relaxed">{row.desc}</p>
                  </div>
                ))}
              </div>
            )}
            {ch.quote && (
              <blockquote className="mt-6 flex gap-3 border-l-2 border-primary/50 pl-5">
                <Quote size={20} className="text-primary/50 flex-shrink-0" />
                <p className="font-display italic text-xl text-foreground/85 leading-relaxed">{ch.quote}</p>
              </blockquote>
            )}
          </motion.article>
        ))}
      </div>

      {/* closing question */}
      <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
        className="glass stats-wow rounded-2xl px-7 py-10 text-center mb-16" data-testid="conference-closing">
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-4">{t("conf.closingLabel")}</p>
        <p className="font-display italic text-xl sm:text-3xl text-gradient-orange leading-snug max-w-2xl mx-auto break-words">{conf.closing_question}</p>
      </motion.div>

      {/* upcoming — prochainement : une carte séparée par conférence (charte graphique) */}
      {upcoming.length > 0 && (
        <div className="mb-16" data-testid="conference-upcoming">
          <p className="text-xs uppercase tracking-[0.22em] text-foreground/40 mb-5 text-center">{t("conf.soonNext")}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {upcoming.map((u, idx) => {
              const open = openSummary === u.id;
              const hasSummary = !!(u.teaser || u.subtitle);
              return (
              <motion.div
                key={u.id}
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: idx * 0.06 }}
                className="glass frame-pulse rounded-2xl border border-primary/15 p-5 hover:border-primary/35 transition-all duration-300" data-testid={`conference-upcoming-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-primary/70 mb-1.5">
                      <CalendarClock size={12} /> {u.month}
                    </span>
                    <h3 className="font-display text-lg sm:text-xl text-foreground/90 leading-snug" data-testid={`conference-upcoming-${idx}-title`}>{u.title}</h3>
                  </div>
                  {u.notified ? (
                    <span data-testid={`conference-notified-${idx}`} className="w-9 h-9 rounded-full border border-primary/30 bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 mt-0.5" title={t("conf.notified")}>
                      <Sparkles size={16} />
                    </span>
                  ) : (
                    <button onClick={() => notifyMe(u.id)} disabled={notifying === u.id} data-testid={`conference-notify-btn-${idx}`}
                      className="btn-glass-soft flex-shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center text-foreground/55 hover:text-primary" title={t("conf.notify")} aria-label={t("conf.notify")}>
                      {notifying === u.id ? <Loader2 size={16} className="animate-spin" /> : <BellRing size={16} />}
                    </button>
                  )}
                </div>
                {u.trailer_url && (
                  <div className="mt-3.5 rounded-xl overflow-hidden border border-primary/15" data-testid={`conference-upcoming-${idx}-trailer`}>
                    <CinematicVideo fade sound soundMask src={u.trailer_url} poster={u.cover_url} testId={`conference-upcoming-${idx}-trailer-video`} caption={u.title} />
                  </div>
                )}
                {hasSummary && (
                  <>
                    <button
                      onClick={() => setOpenSummary(open ? "" : u.id)}
                      data-testid={`conference-summary-btn-${idx}`}
                      aria-expanded={open}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary/80 hover:text-primary transition-colors"
                    >
                      <Info size={13} /> {summaryLabel}
                      <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }} className="overflow-hidden" data-testid={`conference-summary-${idx}`}
                        >
                          <div className="pt-3 border-t border-white/5 mt-3">
                            {u.subtitle && <p className="text-sm text-primary/75 font-display italic mb-2">{u.subtitle}</p>}
                            {u.teaser && u.teaser !== u.subtitle && <p className="text-sm text-foreground/60 leading-relaxed">{u.teaser}</p>}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
                <Link
                  to={`/invitation?ev=conf&id=${encodeURIComponent(u.id)}`}
                  data-testid={`conference-eventqr-${idx}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary/70 hover:text-primary transition-colors"
                >
                  <QrCode size={13} /> {t("conf.eventQr", "QR d'événement")}
                </Link>
              </motion.div>
              );
            })}
          </div>
        </div>
      )}


      {/* Le direct — chat en live */}
      <div data-testid="conference-live">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <h3 className="font-display text-2xl sm:text-3xl flex items-center gap-3">
            <MessageCircle size={24} className="text-primary" /> {t("conf.interveneTitle")}
          </h3>
          {live.status === "live" && (
            <>
              <span data-testid="conference-live-badge" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 text-xs font-semibold uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-red-500 live-dot" /> {t("conf.liveBadge")}
              </span>
              <span data-testid="conference-online" className="inline-flex items-center gap-2 text-sm text-foreground/60">
                <Users size={15} className="text-primary/70" /> {t("conf.online", { count: online })}
              </span>
            </>
          )}
        </div>

        {live.status === "before" && (
          <div className="glass frame-pulse rounded-2xl p-8 text-center" data-testid="conference-countdown">
            <p className="inline-flex items-center gap-2 text-primary/80 uppercase tracking-[0.2em] text-xs mb-3"><CalendarClock size={14} /> {t("conf.liveOn", { date: fmtLiveDate(live.starts_at) })}</p>
            <p className="text-sm text-foreground/50 mb-5">{t("conf.countdownLabel")}</p>
            {cd && (
              <div className="flex items-center justify-center gap-3 sm:gap-6 mb-6">
                {[["d", cd.d], ["h", cd.h], ["m", cd.m], ["s", cd.s]].map(([u, v]) => (
                  <div key={u} className="flex flex-col items-center">
                    <span className="font-display text-gradient-orange text-4xl sm:text-5xl tabular-nums">{String(v).padStart(2, "0")}</span>
                    <span className="text-[11px] uppercase tracking-widest text-foreground/40 mt-1">{t(`conf.${u}Unit`)}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="font-display italic text-foreground/50">{t("conf.notLiveYet")}</p>
          </div>
        )}

        {(live.status === "live" || live.status === "ended") && (
          <>
            {live.status === "ended" && (
              <p className="glass rounded-xl px-5 py-3 mb-4 text-sm text-foreground/60 text-center" data-testid="conference-ended">{t("conf.ended")} · {t("conf.endedDesc")}</p>
            )}
            <div className="glass rounded-2xl p-4 sm:p-5 mb-3 max-h-[440px] overflow-y-auto" data-testid="conference-chat">
              {interventions.length === 0 ? (
                <p className="font-display italic text-foreground/40 py-10 text-center">{t("conf.emptyLive")}</p>
              ) : (
                <div className="space-y-3">
                  {interventions.map((iv, i) => {
                    const mine = iv.voter_id && iv.voter_id === myVid;
                    return (
                      <div key={`${iv.created_at}-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`conference-msg-${i}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-primary/20 border border-primary/30" : "bg-white/[0.04] border border-white/10"}`}>
                          <p className={`text-xs font-display mb-1 ${mine ? "text-primary" : "text-primary/70"}`}>{iv.name}</p>
                          <p className="text-foreground/85 leading-relaxed text-sm whitespace-pre-wrap">{iv.content}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>

            <div className="h-5 mb-2">
              {recentName && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-primary/70 italic px-1" data-testid="conference-recent">
                  {t("conf.justIntervened", { name: recentName })}
                </motion.p>
              )}
            </div>

            {live.status === "live" && (
              <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                <input value={ivName} onChange={(e) => setIvName(e.target.value)} placeholder={t("conf.interveneName")} data-testid="conference-iv-name"
                  className="sm:w-40 bg-black/30 border border-white/10 rounded-full px-4 py-3 outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors text-sm" />
                <input value={ivContent} onChange={(e) => setIvContent(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendIntervention(); }} placeholder={t("conf.chatPlaceholder")} maxLength={800} data-testid="conference-iv-content"
                  className="flex-1 bg-black/30 border border-white/10 rounded-full px-5 py-3 outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors text-sm" />
                <button onClick={sendIntervention} disabled={ivSending || !ivContent.trim()} data-testid="conference-iv-send"
                  className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-50">
                  {ivSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
